export const playerSdkTypes = `
type SDKConfig = {
    triggerDistance: number;
    homeTimer: number;
    resetTimer: number;
    useStack: boolean;
};

enum SensorType {
    GENERIC = "GENERIC",
    NEXMO_NFC = "NEXMO_NFC",
    NEXMO_PICKUP = "NEXMO_PICKUP",
    NEXMO_PRESENCE_XY240 = "NEXMO_PRESENCE_XY240",
    NEXMO_BUTTON = "NEXMO_BUTTON",
    NEXMO_DIGITALIO = "NEXMO_DIGITALIO",
    TH_NFC = "TH_NFC",
    TH_PUSH = "TH_PUSH",
    UHF_NEO_DESKTOP = "UHF_NEO_DESKTOP",
    TOUCH = "TOUCH",
    HOME = "HOME",
    NEXMO_LED = "NEXMO_LED",
    SPLITSCREEN = "SPLITSCREEN",
}

enum SupportedLEDControllers {
    EM6 = "EM6",
}

enum SupportedColors {
    WHITE = 0,
    RED = 1,
    GREEN = 2,
    BLUE = 3,
    YELLOW = 4,
    MAGENTA = 5,
    CYAN = 6,
    ORANGE = 7,
    PINK = 8,
    LIME = 9,
    SPRING_GREEN = "A",
    PURPLE = "B",
    LIGHT_BLUE = "C",
    PALE_RED = "D",
    PALE_GREEN = "E",
    PALE_BLUE = "F",
}

enum SupportedButtonControllers {
    XN_185 = "XN_185",
    XM_350 = "XM_350",
}

enum SupportedTHLinkControllers {
    TH_LINK_1x = "TH_LINK_1x",
    TH_LINK_2x = "TH_LINK_2x",
    TH_LINK_4x = "TH_LINK_4x",
}

enum MetricParamKey {
    SLIDE_ID = "SLIDE_ID",
}

type MetricParam = {
    key: MetricParamKey;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any --- it can really be anything
    values: any[];
};

enum InitialState {
    active = "ACTIVE",
    passive = "PASSIVE",
}

type SDKSensorState = {
    triggerId: string;
    channel: string;
    sensor: SensorType;
    action: InitialState;
};

interface ISettings {
    registerOutput: (
        manufacturer: string | undefined,
        vendorId: string,
        productId: string,
        serialNumber: string | undefined,
        usedController:
            | SupportedLEDControllers
            | SupportedButtonControllers
            | SupportedTHLinkControllers
    ) => void;
    registerSensor: (sensorType: SensorType, channel: string) => void;
    registerSystemState: (state: string) => void;
    setNFCGainLevel: (channel: string, gainLevel: number) => void;
    setLoopVideos: (loopVideos: boolean) => void;
}

interface ILed {
    setRamp(channel: string, color: SupportedColors, speed: number): void;
    setPulse(
        channel: string,
        brightnessStateOne: number,
        brightnessStateTwo: number,
        colorStateOne: SupportedColors,
        colorStateTwo: SupportedColors,
        time: number
    ): void;
    setCustomColor(
        channel: string,
        colorLabel: SupportedColors,
        rgbValue: string
    ): void;
    setCustomCmd(nexmoLedCmd: string): void;
}

interface IMetrics {
    track(metricName: string, metricParams: MetricParam[]): void;
}

interface ITimer {
    startTimeout(duration: number, callback: () => void): any;
    stopTimeout(timerId: any): void;
    startHomeTimer(): void;
    stopHomeTimer(): void;
    startResetTimer(): void;
    stopExistingResetTimer(): void;
}

interface IState {
    sensor: {
        sensorState: {
            triggerId: string;
            channel: string;
            sensor: SensorType;
            action: InitialState;
        }[];
        add(
            sensor: SensorType,
            triggerId: string,
            channel: string,
            action: InitialState
        ): void;
        remove(triggerId: string, channel: string): void;
        isSensorPresentInChannel(sensor: SensorType, channel: string): boolean;
        find: {
            byChannel(sensor: SensorType, channel: string): SDKSensorState[];
            byTriggerId(
                sensor: SensorType,
                triggerId: string
            ): SDKSensorState[];
            byTriggerIdAndChannel(
                sensor: SensorType,
                triggerId: string,
                channel: string
            ): SDKSensorState[];
        };
    };
    generic: {
        get(key: string): unknown;
        set(key: string, value: unknown): void;
    };
}

interface IStack {
    isOldEvent(actionToPrevent: InitialState): boolean;
}

interface IShow {
    singleScreen(
        slideId: string,
        isHomeSlide = false,
        loopVideo?: boolean,
        frameType?: string,
        renderOnScreenNr = 0,
    ): void;
    splitScreen(
        slideIdLeft: string,
        slideIdRight: string,
        loopVideo?: boolean,
        renderOnScreenNr = 0
    ): void;
    homeScreen(): void;
    changeVideoPlaybackState(state: "PAUSE" | "PLAY"): void;
    dispatchCustomEvent(name: string, detail: unknown): void;
    next(): void;
    previous(): void;
}

interface IContent {
    slideIdBy: {
        sensorAndTriggerId(
            sensor: SensorType | string,
            triggerId: string
        ): string | undefined;
    };
}

class PLAYER {
    static executeOnBoot(): void;
    static config: SDKConfig;
    static settings: ISettings;
    static led: ILed;
    static metric: IMetrics;
    static timer: ITimer;
    static state: IState;
    static stack: IStack;
    static show: IShow;
    static content: IContent;
}

const triggerId: string;
const channel: string;
const action: InitialState;
const sensorType: SensorType;
const content: any;

`;
